import { useEffect, useState } from 'react';

import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';

type Trait = {
	name: string;
	value: string | boolean | number;
};

type PersonalizationResponse = {
	attributes: Trait[];
};

const getPersonalizationUserTraitsUrl = (userId: string) =>
	`/gateway/api/tap-delivery/api/v3/personalization/user/${userId}`;

const fetchUserPersonalization = async (
	userId: string,
): Promise<PersonalizationResponse['attributes']> => {
	const response = await fetch(getPersonalizationUserTraitsUrl(userId), {
		method: 'GET',
		credentials: 'include',
		headers: {
			'Content-Type': 'application/json',
			'x-atlassian-mau-ignore': 'true',
		},
	});

	if (!response.ok) {
		throw new Error(`Failed to fetch user personalization: ${response.status}`);
	}

	const { attributes } = await response.json();
	return attributes ?? [];
};

const fetchHasSentRovoChatAudience = async (userId: string): Promise<boolean | undefined> => {
	const userTraits = await fetchUserPersonalization(userId);
	const audience = userTraits?.find((t) => t.name === 'segment_has_sent_rovo_chat_message_aaid');

	if (!audience || typeof audience.value !== 'boolean') {
		return undefined;
	}

	return audience.value;
};

export type HasSentRovoChatMessageResult = {
	/** Whether the fetch has settled (resolved or rejected). False while the request is in-flight. */
	loaded: boolean;
	/**
	 * The value of the `segment_has_sent_rovo_chat_message_aaid` audience trait:
	 * - undefined: no trait found (brand new user) or fetch failed silently
	 * - true: user has previously sent a Rovo Chat message
	 * - false: user has been explicitly flagged as not having sent a message
	 */
	hasSentRovoChatMessage: boolean | undefined;
};

export const useHasUserSentRovoChatMessage = (userId: string): HasSentRovoChatMessageResult => {
	const [hasSentRovoChatMessage, setHasSentRovoChatMessage] = useState<boolean | undefined>(
		undefined,
	);
	const [loaded, setLoaded] = useState(false);
	const { sendAnalyticsEvent } = useAnalytics();

	useEffect(() => {
		let cancelled = false;
		// Reset loaded state when userId changes so callers don't act on stale settled state
		setLoaded(false);
		setHasSentRovoChatMessage(undefined);
		fetchHasSentRovoChatAudience(userId)
			.then((audience) => {
				if (!cancelled) {
					setHasSentRovoChatMessage(audience);
					setLoaded(true);
				}
			})
			.catch(() => {
				if (!cancelled) {
					// Silently fail — fire an analytics event so we can monitor TAP failures.
					// loaded is set to true so callers are not blocked forever; hasSentRovoChatMessage
					// remains undefined, which means we will still evaluate the experiment if all
					// other checks pass. For non-control cohorts, this could incorrectly trigger the
					// experiment for users who have already sent a message before, so we log here to
					// keep an eye on it.
					sendAnalyticsEvent({
						eventType: 'operational',
						action: 'failed',
						actionSubject: 'fetchHasSentRovoChatMessage',
						attributes: {
							reason:
								'TAP failure - `segment_has_sent_rovo_chat_message_aaid` fetch failed, silently failing',
						},
					});
					setLoaded(true);
				}
			});
		return () => {
			cancelled = true;
		};
	}, [userId, sendAnalyticsEvent]);

	return { hasSentRovoChatMessage, loaded };
};
